import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Loader from "../../common/loader";
import Breadcrums from "../../common/breadcrumbs";
import {
  formateDateWithMonth,
  handleServerValidations,
} from "../../../../utils/commonfunction";
import * as orderServices from "../../services/order.services";
import Swal from "sweetalert2";
import { SWAL_SETTINGS } from "../../../../utils/Constants";

const OrderView = () => {
  const { t } = useTranslation();
  const { id: order_id } = useParams();
  const [order, setOrder] = useState(null);
  const [loader, setLoader] = useState(true);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);

  const breadcrumbs = [
    { title: "Dashboard", url: "/admin/dashboard" },
    { title: "Orders", url: "/admin/order/list/1" },
    { title: `Order #${order?.order_tracking_id || "..."}`, url: "" },
  ];

  useEffect(() => {
    orderServices
      .GetOrderDetails(order_id)
      .then((res) => {
        setOrder(res.data);
        setLoader(false);
      })
      .catch((err) => {
        console.log(err);
        setLoader(false);
      });
  }, [order_id]);

  const executeManageReturn = (payload) => {
    orderServices
      .ManageReturn(payload)
      .then((response) => {
        if (response.success) {
          Swal.fire({
            icon: "success",
            text: response.message || "Return status updated!",
            ...SWAL_SETTINGS,
          });
          // Refresh order data
          orderServices
            .GetOrderDetails(order_id)
            .then((res) => setOrder(res.data));
        } else {
          Swal.fire({
            icon: "error",
            text: handleServerValidations(response),
            ...SWAL_SETTINGS,
          });
        }
      })
      .catch(() => {
        Swal.fire({
          icon: "error",
          text: "Failed to update",
          ...SWAL_SETTINGS,
        });
      });
  };

  const manageReturn = (itemId, action) => {
    const actions = {
      approve: "Approve",
      reject: "Reject",
      process: "Process Refund",
    };

    if (action === "reject") {
      Swal.fire({
        title: "Reject Return Request",
        text: "Please provide a reason for rejecting this return request:",
        input: "textarea",
        inputPlaceholder: "Enter rejection reason here...",
        inputAttributes: {
          "aria-label": "Enter rejection reason",
          rows: 3,
        },
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "Reject Return",
        cancelButtonText: "Cancel",
        inputValidator: (value) => {
          if (!value || !value.trim()) {
            return "Rejection reason is required!";
          }
        },
      }).then((result) => {
        if (result.isConfirmed) {
          const payload = {
            order_id: order_id,
            item_id: itemId,
            action: "reject",
            reason: result.value.trim(),
          };
          executeManageReturn(payload);
        }
      });
      return;
    }

    Swal.fire({
      title: "Are you sure?",
      text: `${actions[action]} this return request?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: `Yes, ${actions[action]}`,
    }).then((result) => {
      if (result.isConfirmed) {
        const payload = {
          order_id: order_id,
          item_id: itemId,
          action: action,
        };
        executeManageReturn(payload);
      }
    });
  };

  const handleViewInvoice = async () => {
    const newTab = window.open("about:blank", "_blank");
    if (newTab) {
      newTab.document.write(
        "<html><head><title>Loading Invoice...</title></head><body style='display:flex;align-items:center;justify-content:center;height:100vh;margin:0;font-family:sans-serif;background:#f8fafc;'><div style='text-align:center;'><h3 style='color:#334155;margin-bottom:8px;'>Generating Invoice...</h3><p style='color:#64748b;margin:0;'>Please wait while your PDF invoice is prepared.</p></div></body></html>"
      );
    }
    try {
      setInvoiceLoading(true);
      const res = await orderServices.DownloadInvoice(order_id);
      const blob = new Blob([res], { type: "application/pdf" });
      const fileURL = window.URL.createObjectURL(blob);
      if (newTab) {
        newTab.location.href = fileURL;
      } else {
        window.open(fileURL, "_blank");
      }
    } catch (err) {
      if (newTab) newTab.close();
      console.error("Error viewing invoice:", err);
      Swal.fire({
        icon: "error",
        text: "Failed to generate invoice. Please try again.",
        ...SWAL_SETTINGS,
      });
    } finally {
      setInvoiceLoading(false);
    }
  };

  const handleSendInvoiceEmail = () => {
    Swal.fire({
      title: "Send Invoice Email?",
      text: "Send the official tax invoice PDF attached in an email to the customer?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Send Email",
      cancelButtonText: "Cancel",
      ...SWAL_SETTINGS,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setEmailLoading(true);
          const res = await orderServices.SendInvoiceEmail(order_id);
          if (res.success) {
            Swal.fire({
              icon: "success",
              text: res.message || "Invoice email sent successfully!",
              ...SWAL_SETTINGS,
            });
          } else {
            Swal.fire({
              icon: "error",
              text: res.message || "Failed to send invoice email.",
              ...SWAL_SETTINGS,
            });
          }
        } catch (error) {
          console.error("Error sending invoice email:", error);
          Swal.fire({
            icon: "error",
            text: "Failed to send invoice email. Please try again.",
            ...SWAL_SETTINGS,
          });
        } finally {
          setEmailLoading(false);
        }
      }
    });
  };

  const handleCancelOrder = () => {
    Swal.fire({
      title: "Cancel Order",
      text: "Please provide a reason for cancelling this order. This will be sent to the customer via email and notification:",
      input: "textarea",
      inputPlaceholder: "Enter cancellation reason...",
      inputAttributes: {
        "aria-label": "Cancellation reason",
        rows: 3,
      },
      showCancelButton: true,
      cancelButtonText: "Close",
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Cancel Order",
      inputValidator: (value) => {
        if (!value || !value.trim()) {
          return "Cancellation reason is required!";
        }
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const reason = result.value.trim();
          const formData = new FormData();
          formData.append("order_id", order_id);
          formData.append("new_status", "Cancelled");
          formData.append("reason", reason);

          const response = await orderServices.UpdateOrderStatus(formData);
          if (response && response.success) {
            Swal.fire({
              icon: "success",
              text: "Order cancelled and customer notified via email & notification!",
              ...SWAL_SETTINGS,
            });
            orderServices
              .GetOrderDetails(order_id)
              .then((res) => setOrder(res.data));
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        } catch (error) {
          console.error("Cancel order error:", error);
          Swal.fire({
            icon: "error",
            text: "Failed to cancel order. Please try again.",
            ...SWAL_SETTINGS,
          });
        }
      }
    });
  };

  if (loader) return <Loader />;
  if (!order) return <div className="text-center p-5">Order not found</div>;

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <div className="animation_fade">
        <div className="card custom-card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="main-content-label mb-0">
                Order Details - #{order.order_tracking_id}
              </h5>
              <div>
                <button
                  className="btn btn-primary me-2"
                  onClick={handleViewInvoice}
                  disabled={invoiceLoading}
                  title="View Invoice"
                >
                  {invoiceLoading ? (
                    <i className="fa fa-spinner fa-spin me-1"></i>
                  ) : (
                    <i className="fa fa-file-invoice me-1"></i>
                  )}
                  View Invoice
                </button>
                <button
                  className="btn btn-outline-success me-2"
                  onClick={handleSendInvoiceEmail}
                  disabled={emailLoading}
                  title="Send Invoice PDF to Customer Email"
                >
                  {emailLoading ? (
                    <i className="fa fa-spinner fa-spin me-1"></i>
                  ) : (
                    <i className="fa fa-envelope me-1"></i>
                  )}
                  Email Invoice
                </button>
                {["Placed", "Processing", "Shipped"].includes(order.status) && (
                  <button
                    className="btn btn-danger me-2"
                    onClick={handleCancelOrder}
                    title="Cancel Order"
                  >
                    <i className="fa fa-ban me-1"></i> Cancel Order
                  </button>
                )}
                <Link to="/admin/order/list/1" className="btn btn-secondary">
                  ← Back to Orders
                </Link>
              </div>
            </div>

            <div className="row">
              {/* Order Summary */}
              <div className="col-lg-4 mb-4">
                <div className="card border shadow-sm">
                  <div className="card-header bg-primary text-white">
                    <h6 className="mb-0">Order Summary</h6>
                  </div>
                  <div className="card-body">
                    <p>
                      <strong>Status:</strong>
                      <span
                        className={`badge ms-2 ${
                          order.status === "Delivered"
                            ? "bg-success"
                            : order.status === "Cancelled"
                              ? "bg-danger"
                              : "bg-warning"
                        }`}
                      >
                        {order.status}
                      </span>
                    </p>
                    {order.status === "Cancelled" && order.cancel_reason && (
                      <p className="text-danger mb-2">
                        <strong>Reason:</strong> {order.cancel_reason}
                      </p>
                    )}
                    {order.status === "Cancelled" &&
                      order.refund_status &&
                      order.refund_status !== "None" && (
                        <p className="text-info mb-2">
                          <strong>Refund Status:</strong> {order.refund_status}
                          {order.refund_amount
                            ? ` (₹${order.refund_amount})`
                            : ""}
                        </p>
                      )}
                    <p>
                      <strong>Date:</strong>{" "}
                      {formateDateWithMonth(order.createdAt)}
                    </p>
                    <p>
                      <strong>Payment:</strong> {order.payment_method}
                    </p>
                    <p>
                      <strong>Delivery:</strong> {order.delivery_type}
                    </p>
                    <hr />
                    <p>
                      <strong>Subtotal:</strong> ₹{order.subtotal}
                    </p>
                    {order.total_discount > 0 && (
                      <p className="text-success">
                        <strong>Discount:</strong> -₹{order.total_discount}
                      </p>
                    )}
                    {order.total_gst > 0 && (
                      <p className="text-info">
                        <strong>GST (Tax):</strong> +₹{order.total_gst}
                      </p>
                    )}
                    <p>
                      <strong>Delivery Charges:</strong> ₹
                      {order.delivery_charges}
                    </p>
                    <h5 className="text-primary">
                      <strong>Total:</strong> ₹{order.total}
                    </h5>
                  </div>
                </div>
              </div>

              {/* Customer Info */}
              <div className="col-lg-4 mb-4">
                <div className="card border shadow-sm">
                  <div className="card-header bg-info text-white">
                    <h6 className="mb-0">Customer</h6>
                  </div>
                  <div className="card-body">
                    <p>
                      <strong>{order.user.name}</strong>
                    </p>
                    {/* <p>{order.user.email}</p> */}
                    <p>
                      {order.user.phone_number || order.user.email || "N/A"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="col-lg-4 mb-4">
                <div className="card border shadow-sm">
                  <div className="card-header bg-success text-white">
                    <h6 className="mb-0">Shipping Address</h6>
                  </div>
                  <div className="card-body">
                    <p>
                      <strong>{order.address.name}</strong>
                    </p>
                    <p>{order.address.user_phone_number}</p>
                    <p>{order.address.full_address}</p>
                    <p>
                      {order.address.city}, {order.address.state} -{" "}
                      {order.address.zip}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Ordered Items with Return Management */}
            <h5 className="mt-4 mb-3">Ordered Items</h5>
            <div className="table-responsive">
              <table className="table table-bordered table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Item</th>
                    <th>Qty</th>
                    <th>Price</th>
                    <th>GST (Tax)</th>
                    <th>Total</th>
                    <th>Return Status</th>
                    <th>Return Reason</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((item) => (
                    <tr key={item._id}>
                      <td>
                        <div className="d-flex align-items-center">
                          {item.image_url ? (
                            <img
                              src={item.image_url}
                              alt={item.name}
                              width="60"
                              className="me-3 rounded"
                            />
                          ) : (
                            <div
                              className="bg-light border rounded me-3"
                              style={{ width: 60, height: 60 }}
                            ></div>
                          )}
                          <div>
                            <strong>{item.name}</strong>
                            {/* {item.remedy_details?.description && (
                              <small className="d-block text-muted">
                                {item.remedy_details.description.substring(0, 80)}...
                              </small>
                            )} */}
                          </div>
                        </div>
                      </td>
                      <td className="text-center">{item.quantity}</td>
                      <td>
                        ₹{item.price}
                        {item.discount > 0 && (
                          <small className="d-block text-muted text-decoration-line-through">
                            ₹{item.original_price || item.price} ({item.discount}% off)
                          </small>
                        )}
                      </td>
                      <td>
                        {item.gst > 0 ? (
                          <>
                            <span>{item.gst}%</span>
                            <small className="d-block text-muted">
                              +₹{((item.gst_amount || 0) * item.quantity).toFixed(2)}
                            </small>
                          </>
                        ) : (
                          "0%"
                        )}
                      </td>
                      <td>
                        ₹{item.total ? Number(item.total).toFixed(2) : ((item.price + (item.gst_amount || 0)) * item.quantity).toFixed(2)}
                      </td>
                      <td className="text-center">
                        <span
                          className={`badge ${
                            item.return_status === "None"
                              ? "bg-secondary"
                              : item.return_status === "Requested"
                                ? "bg-warning"
                                : item.return_status === "Approved"
                                  ? "bg-info"
                                  : item.return_status === "Processed"
                                    ? "bg-success"
                                    : item.return_status === "Rejected"
                                      ? "bg-danger"
                                      : "bg-light"
                          }`}
                        >
                          {item.return_status}
                        </span>
                      </td>
                      <td>
                        <div>{item.return_reason || "-"}</div>
                        {item.return_status === "Rejected" && item.return_reject_reason && (
                          <div className="small text-danger mt-1">
                            <strong>Reject Reason:</strong> {item.return_reject_reason}
                          </div>
                        )}
                      </td>
                      <td>
                        {/* Return Actions */}
                        {item.return_status === "Requested" && (
                          <>
                            <button
                              className="btn btn-sm btn-success me-1"
                              onClick={() => manageReturn(item._id, "approve")}
                            >
                              Approve
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => manageReturn(item._id, "reject")}
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {item.return_status === "Approved" && (
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => manageReturn(item._id, "process")}
                          >
                            Process Refund
                          </button>
                        )}
                        {["None", "Rejected"].includes(item.return_status) && (
                          <span className="text-muted small">No action</span>
                        )}
                        {item.return_status === "Processed" && (
                          <div className="text-start">
                            <span className="d-block text-success small">
                              Refund Processed
                            </span>
                            {item.refund_id && (
                              <small
                                className="d-block text-muted"
                                style={{ fontSize: "0.75rem" }}
                              >
                                Ref: {item.refund_id}
                              </small>
                            )}
                            {item.refund_date && (
                              <small
                                className="d-block text-muted"
                                style={{ fontSize: "0.75rem" }}
                              >
                                Date: {formateDateWithMonth(item.refund_date)}
                              </small>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Payment Transaction */}
            {order.transaction && (
              <div className="mt-5">
                <h5>Payment Details</h5>
                <div className="card border shadow-sm">
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <p>
                          <strong>Amount Paid:</strong> ₹
                          {order.transaction.amount}
                        </p>
                        <p>
                          <strong>Status:</strong>
                          <span
                            className={`badge ms-2 ${order.transaction.status === "Success" ? "bg-success" : "bg-danger"}`}
                          >
                            {order.transaction.status}
                          </span>
                        </p>
                      </div>
                      <div className="col-md-6">
                        <p>
                          <strong>Method:</strong> {order.transaction.method}
                        </p>
                        <p>
                          <strong>Transaction ID:</strong>{" "}
                          {order.transaction.reference_id || "N/A"}
                        </p>
                        <p>
                          <strong>Date:</strong>{" "}
                          {formateDateWithMonth(order.transaction.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderView;
